import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, Clock, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import { getPayInfo, createPayCheckout } from "../lib/api";

const BookingPayPage = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | already-paid | error
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id || !token) {
      setState("error");
      setError("This payment link is missing details. Please use the link from the email.");
      return;
    }
    getPayInfo(id, token)
      .then((r) => {
        setInfo(r);
        if (r.payment_status === "paid") setState("already-paid");
        else setState("ready");
      })
      .catch((e) => {
        setState("error");
        setError(e?.response?.data?.detail || "Payment link is invalid or expired.");
      });
  }, [id, token]);

  const pay = async () => {
    setSubmitting(true);
    try {
      const r = await createPayCheckout(id, token, window.location.origin);
      if (r.checkout_url) {
        window.location.href = r.checkout_url;
      } else {
        toast.error("Could not start checkout");
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-pay-page">
      <Navigation />
      <div className="min-h-screen pt-32 pb-20 bg-background">
        <div className="container-x">
          <div className="max-w-2xl mx-auto bg-white border border-border p-10 md:p-14">
            {state === "loading" && (
              <div className="text-center">
                <Loader2 size={32} className="text-secondary mx-auto animate-spin" />
                <p className="mt-4 text-muted-foreground">Loading your booking…</p>
              </div>
            )}

            {state === "error" && (
              <div className="text-center">
                <AlertCircle size={42} className="text-destructive mx-auto" />
                <h1 className="font-serif text-3xl md:text-4xl text-primary mt-6">Link problem</h1>
                <p className="text-muted-foreground mt-3">{error}</p>
                <Link to="/" className="btn-outline mt-6">Back home</Link>
              </div>
            )}

            {state === "already-paid" && info && (
              <div className="text-center">
                <CheckCircle2 size={42} className="text-secondary mx-auto" />
                <div className="overline mt-6">Already paid</div>
                <h1 className="font-serif text-3xl md:text-4xl text-primary mt-3">
                  This booking is already paid.
                </h1>
                <p className="text-muted-foreground mt-3">
                  You&rsquo;re all set for {info.booking_date}. Check your email for the receipt.
                </p>
                <Link to="/" className="btn-primary mt-6">Back home</Link>
              </div>
            )}

            {state === "ready" && info && (
              <>
                <div className="overline mb-3">Complete your payment</div>
                <h1 className="font-serif text-3xl md:text-4xl text-primary text-balance mb-2">
                  Your partner charter is approved.
                </h1>
                <p className="text-muted-foreground mb-8">
                  Pay the {info.payment_type} below to lock in your date.
                </p>

                <div className="border border-border p-6 bg-muted/30 mb-8">
                  <div className="text-lg font-medium text-primary mb-2">{info.charter_title}</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Clock size={14} /> {info.booking_date} · {info.start_time}–{info.end_time}
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Users size={14} /> {info.guests} guests
                    </div>
                  </div>
                  <div className="border-t border-border mt-4 pt-4 flex justify-between items-end">
                    <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                      {info.payment_type === "deposit" ? "Deposit due" : "Total due"}
                    </span>
                    <span className="font-serif text-3xl text-primary">
                      €{Number(info.amount_due).toLocaleString()}
                    </span>
                  </div>
                </div>

                <button
                  onClick={pay}
                  disabled={submitting}
                  className="btn-primary w-full"
                  data-testid="pay-link-submit"
                >
                  {submitting ? (
                    <><Loader2 size={14} className="animate-spin" /> Redirecting to Stripe…</>
                  ) : (
                    <>Pay €{Number(info.amount_due).toLocaleString()} securely <ArrowRight size={14} /></>
                  )}
                </button>
                <div className="text-xs text-muted-foreground mt-4 text-center">
                  Payments processed by Stripe. Your card details never touch our server.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default BookingPayPage;
